import { useEffect, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ProductTypeName } from '@electricjs/core_entity-client';
import { Box, RadioGroup, Link, Carousel, sizes } from '@electricjs/arc';

import { PRODUCT_TYPES_BY_TYPE_GROUP } from '@/constants/storefront';
import { StorefrontProductTypeGroup } from '@/types/storefront';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { TypeGroupRadioTile } from './TypeGroupRadioTile';

export type ProductTypeGroupSelectorProps = {
  selectedProductTypeGroup: StorefrontProductTypeGroup;
  onProductTypeGroupSelect: (group: StorefrontProductTypeGroup) => void;
  onProductTypesSelectFromGroup: (types: ProductTypeName[]) => void;
  laptopRecommendationUrl?: string;
};

export const ProductTypeGroupSelector = ({
  selectedProductTypeGroup,
  onProductTypeGroupSelect,
  onProductTypesSelectFromGroup,
  laptopRecommendationUrl,
}: ProductTypeGroupSelectorProps) => {
  const { t } = useTranslation('storefront');

  useEffect(() => {
    // override all selected types (using the types got from the group)
    onProductTypesSelectFromGroup(
      PRODUCT_TYPES_BY_TYPE_GROUP[selectedProductTypeGroup]
    );
  }, [selectedProductTypeGroup, onProductTypesSelectFromGroup]);

  const { storefrontTablets, storefrontPhones } = useFlags();

  return (
    <Box width="100%">
      <RadioGroup
        id="storefront-product-type-group-selector"
        aria-label={t('productTypeGroupSelector.ariaLabel')}
        selectTilesBorderWidth={sizes.size_2}
        state={selectedProductTypeGroup}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onProductTypeGroupSelect(e.target.value as StorefrontProductTypeGroup)
        }>
        <Carousel
          scrollAmountPerClick={320}
          carouselRowProps={{
            padding: sizes.size_2, // the same value of the selectTilesBorderWidth
            gap: '16px',
          }}>
          <TypeGroupRadioTile
            name={t('productTypeGroupSelector.groups.laptops')}
            icons={['laptop']}
            value={StorefrontProductTypeGroup.Laptops}
            checked={
              selectedProductTypeGroup === StorefrontProductTypeGroup.Laptops
            }
          />
          <TypeGroupRadioTile
            name={t('productTypeGroupSelector.groups.monitors')}
            icons={['monitor']}
            value={StorefrontProductTypeGroup.Monitors}
            checked={
              selectedProductTypeGroup === StorefrontProductTypeGroup.Monitors
            }
          />
          <TypeGroupRadioTile
            name={t('productTypeGroupSelector.groups.desktops')}
            icons={['desktop']}
            value={StorefrontProductTypeGroup.Desktops}
            checked={
              selectedProductTypeGroup === StorefrontProductTypeGroup.Desktops
            }
          />
          {storefrontTablets && (
            <TypeGroupRadioTile
              name={t('productTypeGroupSelector.groups.tablets')}
              icons={['tablet']}
              value={StorefrontProductTypeGroup.Tablets}
              checked={
                selectedProductTypeGroup === StorefrontProductTypeGroup.Tablets
              }
            />
          )}
          {storefrontPhones && (
            <TypeGroupRadioTile
              name={t('productTypeGroupSelector.groups.phones')}
              icons={['telephone']}
              value={StorefrontProductTypeGroup.Phones}
              checked={
                selectedProductTypeGroup === StorefrontProductTypeGroup.Phones
              }
            />
          )}
          <TypeGroupRadioTile
            name={t('productTypeGroupSelector.groups.keyboardMouse')}
            icons={['keyboard', 'mouse']}
            value={StorefrontProductTypeGroup.KeyboardMouse}
            checked={
              selectedProductTypeGroup ===
              StorefrontProductTypeGroup.KeyboardMouse
            }
          />
          <TypeGroupRadioTile
            name={t('productTypeGroupSelector.groups.docks')}
            icons={['dongle']}
            value={StorefrontProductTypeGroup.Docks}
            checked={
              selectedProductTypeGroup === StorefrontProductTypeGroup.Docks
            }
          />
          <TypeGroupRadioTile
            name={t('productTypeGroupSelector.groups.audioVideo')}
            icons={['audio', 'webcam']}
            value={StorefrontProductTypeGroup.AudioVideo}
            checked={
              selectedProductTypeGroup === StorefrontProductTypeGroup.AudioVideo
            }
          />
          <TypeGroupRadioTile
            name={t('productTypeGroupSelector.groups.misc')}
            icons={[]}
            value={StorefrontProductTypeGroup.Misc}
            checked={
              selectedProductTypeGroup === StorefrontProductTypeGroup.Misc
            }
          />
        </Carousel>
      </RadioGroup>
      {selectedProductTypeGroup === StorefrontProductTypeGroup.Laptops &&
        laptopRecommendationUrl && (
          <Box mt="1.6rem">
            <Link
              href={laptopRecommendationUrl}
              rel="noopener noreferrer"
              target="_blank">
              {t('productTypeGroupSelector.laptopRecommendationLink')}
            </Link>
          </Box>
        )}
    </Box>
  );
};
